import { axios } from "..";
import { IEmailDistributionList } from "../interfaces/models/IEmailDistributionList";

export const EMAIL_DISTRIBUTION_LIST_DROPDOWN_FIELDS = [
  "id",
  "name",
  "emailTemplateIds",
] as const;

export const EMAIL_DISTRIBUTION_LIST_DETAIL_FIELDS = [
  "id",
  "name",
  "filter",
  "isTestList",
  "emailTemplateIds",
] as const;

/** Lightweight fields for list pages — excludes heavy recipient filter JSON. */
export const EMAIL_DISTRIBUTION_LIST_SUMMARY_FIELDS = [
  "id",
  "name",
  "isTestList",
  "emailTemplateIds",
] as const;

export const EMAIL_DISTRIBUTION_LIST_PAGE_SIZE = 50;

export const mergeEmailTemplateIds = (
  existing: string[] | undefined,
  ...toAdd: Array<string | undefined>
): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  [...(existing ?? []), ...toAdd].forEach((value) => {
    const normalized = String(value ?? "").trim();
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    result.push(normalized);
  });

  return result;
};

interface IEmailDistributionListsQuery {
  filter?: Record<string, unknown>;
  fields?: string[];
  sort?: { field: string; order: "asc" | "desc" };
  pagination?: { pageIndex: number; pageSize: number };
  count?: boolean;
}

export const buildRecipientListDropdownFilter = (
  searchQuery?: string,
  emailTemplateNumber?: string,
): Record<string, unknown> => {
  const filter: Record<string, unknown> = {
    isTestList: { value: true, comparisonType: "NOT_EQUAL" },
  };

  const trimmedQuery = searchQuery?.trim();
  if (trimmedQuery) {
    filter.name = { value: trimmedQuery, comparisonType: "CONTAINS" };
  }

  const trimmedTemplateNumber = emailTemplateNumber?.trim();
  if (trimmedTemplateNumber) {
    filter.emailTemplateIds = {
      value: trimmedTemplateNumber,
      comparisonType: "ARRAY_CONTAINS",
    };
  }

  return filter;
};

export const getEmailDistributionLists = async (
  query?: IEmailDistributionListsQuery,
): Promise<IEmailDistributionList[]> => {
  try {
    const params = new URLSearchParams();

    if (query?.filter && Object.keys(query.filter).length > 0) {
      params.append("filter", JSON.stringify(query.filter));
    }
    if (query?.fields && query.fields.length > 0) {
      params.append("fields", JSON.stringify(query.fields));
    }
    if (query?.sort) {
      params.append("sort", JSON.stringify(query.sort));
    }
    if (query?.pagination) {
      params.append("pagination", JSON.stringify(query.pagination));
    }
    if (query?.count) {
      params.append("count", "true");
    }

    const queryString = params.toString();
    const path = queryString
      ? `./emailDistributionList?${queryString}`
      : "./emailDistributionList";
    const response = await axios.Get(path);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    return [];
  }
};

export const getEmailDistributionListCount = async (
  filter?: Record<string, unknown>,
): Promise<number> => {
  try {
    const params = new URLSearchParams();
    params.append("count", "true");
    if (filter && Object.keys(filter).length > 0) {
      params.append("filter", JSON.stringify(filter));
    }
    const response = await axios.Get(`./emailDistributionList?${params.toString()}`);
    return typeof response.data === "number" ? response.data : 0;
  } catch (error) {
    return 0;
  }
};

export const getEmailDistributionListById = async (
  id: string,
): Promise<IEmailDistributionList | null> => {
  if (!id) {
    return null;
  }

  const lists = await getEmailDistributionLists({
    filter: { id: { value: id, comparisonType: "EQUAL" } },
    fields: [...EMAIL_DISTRIBUTION_LIST_DETAIL_FIELDS],
  });

  return lists.find((list) => list.id === id) || lists[0] || null;
};

export const createEmailDistributionList = async (
  distributionList: IEmailDistributionList,
): Promise<IEmailDistributionList | null> => {
  try {
    const response = await axios.Post("./emailDistributionList", distributionList);
    return response?.data || null;
  } catch (error) {
    return null;
  }
};

export const updateEmailDistributionList = async (
  distributionList: IEmailDistributionList,
): Promise<IEmailDistributionList | null> => {
  try {
    const response = await axios.Put("./emailDistributionList", distributionList);
    return response?.data || null;
  } catch (error) {
    return null;
  }
};

/** Updates only emailTemplateIds; other document fields are preserved server-side. */
export const patchEmailDistributionListTemplateIds = async (
  id: string,
  emailTemplateIds: string[],
): Promise<IEmailDistributionList | null> => {
  if (!id) {
    return null;
  }
  try {
    const response = await axios.Put("./emailDistributionList", {
      id,
      emailTemplateIds,
    });
    return response?.data || null;
  } catch (error) {
    return null;
  }
};

/** Links a recipient list to a template email # (merges into emailTemplateIds). */
export const associateRecipientListWithTemplate = async (
  listId: string,
  emailTemplateNumber: string,
): Promise<IEmailDistributionList | null> => {
  const templateNumber = emailTemplateNumber.trim();
  if (!listId || !templateNumber) {
    return null;
  }

  const current = await getEmailDistributionListById(listId);
  if (!current) {
    return null;
  }

  const alreadyLinked = (current.emailTemplateIds ?? []).some(
    (value) => String(value).trim() === templateNumber,
  );
  if (alreadyLinked) {
    return current;
  }

  const nextIds = mergeEmailTemplateIds(current.emailTemplateIds, templateNumber);
  return patchEmailDistributionListTemplateIds(listId, nextIds);
};

export const fetchAllEmailDistributionLists = async (): Promise<
  IEmailDistributionList[]
> => fetchAllEmailDistributionListSummaries();

export const fetchAllEmailDistributionListSummaries = async (): Promise<
  IEmailDistributionList[]
> => {
  const pageSize = 500;
  const query = {
    fields: [...EMAIL_DISTRIBUTION_LIST_SUMMARY_FIELDS],
    sort: { field: "name", order: "asc" as const },
  };

  const total = await getEmailDistributionListCount();
  if (total > 0) {
    const pageCount = Math.ceil(total / pageSize);
    const batches = await Promise.all(
      Array.from({ length: pageCount }, (_, pageIndex) =>
        getEmailDistributionLists({
          ...query,
          pagination: { pageIndex, pageSize },
        }),
      ),
    );
    return batches.flat();
  }

  const all: IEmailDistributionList[] = [];
  let pageIndex = 0;
  while (true) {
    const batch = await getEmailDistributionLists({
      ...query,
      pagination: { pageIndex, pageSize },
    });
    all.push(...batch);
    if (batch.length < pageSize) {
      break;
    }
    pageIndex += 1;
  }
  return all;
};
